import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import { Star } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import MatchSearchLoader from "../common/MatchSearchLoader";
import { colors } from "../../theme/colors";
import CascadeTimer from "./CascadeTimer";
import LobbySectionDiamond from "./LobbySectionDiamond";
import LobbySearchingInfoBar from "./LobbySearchingInfoBar";
import SmallerMatchOfferDialog from "./SmallerMatchOfferDialog.jsx";
import { LOBBY_EXIT_BUTTON_SX } from "../common/dialogActionButtonSx";
import {
    LOBBY_OURO_BORDER,
    LOBBY_OURO_SURFACE,
    MOBILE_NAV_MQ,
    SHORT_VIEWPORT_MQ,
} from "./lobbyPageStyles";
import {
    playSmallerMatchOfferSoundOnce,
    clearSmallerMatchOfferSound,
} from "../../universal/matchNotificationSounds";
import { getSmallerMatchOfferViewModel } from "./smallerMatchOfferViewModel";

const MotionBox = motion(Box);

const SEARCH_TIPS = [
    "Tip: widen your ELO gap in Preferences to shorten queue times.",
    "Tip: enabling more regions increases your match pool.",
    "Tip: parties with open slots fill faster during peak hours.",
    "Tip: Trust Factor is recalculated after every match — play clean.",
];

const TIP_INTERVAL_MS = 4500;
const TIP_FADE_MS = 300;

export default function LobbySearchingStatus({
    timestamp,
    onCancel,
    canCancel = true,
    isParty = false,
    members = [],
    maxPlayers = 5,
    regionsLabel = "All regions",
    averageElo = null,
    playersSearching = null,
    serversOnline = null,
    compatibleCount = null,
    mapsLabel = "All maps",
    smallerMatchOffer = null,
    canActOnSmallerMatch = canCancel,
    onSmallerMatchOptIn,
    onSmallerMatchDecline,
}) {
    const [tipIndex, setTipIndex] = useState(0);
    const [tipOpaque, setTipOpaque] = useState(true);
    const {
        teamSize: smallerMatchTeamSize,
        label: smallerMatchLabel,
        status: smallerMatchStatus,
        show: showSmallerMatchOffer,
    } = getSmallerMatchOfferViewModel(smallerMatchOffer);

    useEffect(() => {
        const reduceMotion =
            typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

        let fadeTimer = null;
        const id = window.setInterval(() => {
            if (reduceMotion) {
                setTipIndex((i) => (i + 1) % SEARCH_TIPS.length);
                return;
            }
            setTipOpaque(false);
            fadeTimer = window.setTimeout(() => {
                setTipIndex((i) => (i + 1) % SEARCH_TIPS.length);
                setTipOpaque(true);
            }, TIP_FADE_MS);
        }, TIP_INTERVAL_MS);

        return () => {
            window.clearInterval(id);
            if (fadeTimer) window.clearTimeout(fadeTimer);
        };
    }, []);

    useEffect(() => {
        if (showSmallerMatchOffer && smallerMatchStatus === "offered") {
            playSmallerMatchOfferSoundOnce(smallerMatchOffer);
            return undefined;
        }
        if (!smallerMatchOffer) {
            clearSmallerMatchOfferSound();
        }
        return undefined;
    }, [showSmallerMatchOffer, smallerMatchStatus, smallerMatchTeamSize, smallerMatchOffer]);

    return (
        <MotionBox
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{
                duration: 0.5,
                delay: 0.12,
                ease: [0.2, 0.7, 0.3, 1],
            }}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: 0,
                flex: "1 1 auto",
                alignSelf: "stretch",
                mx: "auto",
                maxWidth: { xs: "100%", xl: "52rem" },
                px: "1.25rem",
                boxSizing: "border-box",
                pointerEvents: "auto",
                [MOBILE_NAV_MQ]: {
                    px: "0.85rem",
                    maxWidth: "100%",
                },
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    flex: "1 1 auto",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0,
                }}
            >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: "22rem",
                }}
            >
            <CascadeTimer timestamp={timestamp} />

            <Box
                role="status"
                aria-live="polite"
                aria-atomic="true"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    mt: { xs: "0.7rem", xl: "0.8rem" },
                    [MOBILE_NAV_MQ]: {
                        mt: "0.55rem",
                        gap: "0.42rem",
                    },
                }}
            >
                <MatchSearchLoader size="compact" diameter={22} />
                <Typography
                    component="p"
                    sx={{
                        m: 0,
                        fontSize: { xs: "0.68rem", xl: "0.72rem" },
                        fontWeight: 600,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: alpha(colors.primary.main, 0.88),
                        lineHeight: 1,
                        [MOBILE_NAV_MQ]: {
                            fontSize: "0.62rem",
                            letterSpacing: "0.14em",
                        },
                    }}
                >
                    Searching for a match
                </Typography>
            </Box>

            <Typography
                component="p"
                sx={{
                    m: 0,
                    mt: "0.32rem",
                    fontSize: { xs: "0.68rem", xl: "0.72rem" },
                    fontWeight: 500,
                    letterSpacing: "0.06em",
                    color: alpha(colors.text.secondary, 0.62),
                    lineHeight: 1.2,
                    [MOBILE_NAV_MQ]: {
                        fontSize: "0.62rem",
                    },
                }}
            >
                {isParty
                    ? `${members.length}/${maxPlayers || 5} party`
                    : "Solo"}
            </Typography>

            {canCancel ? (
                <Button
                    type="button"
                    variant="contained"
                    disableElevation
                    onClick={onCancel}
                    sx={{
                        ...LOBBY_EXIT_BUTTON_SX,
                        minWidth: { xs: "12.5rem", xl: "16.5rem" },
                        height: { xs: "2.55rem", xl: "3.05rem" },
                        px: { xs: "1.35rem", xl: "2.1rem" },
                        fontSize: { xs: "0.72rem", xl: "0.78rem" },
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        mt: { xs: "2.35rem", xl: "2.5rem" },
                        "&:hover": {
                            ...LOBBY_EXIT_BUTTON_SX["&:hover"],
                            transform: "none",
                        },
                        "&:active": {
                            ...LOBBY_EXIT_BUTTON_SX["&:active"],
                            transform: "none",
                        },
                        [MOBILE_NAV_MQ]: {
                            ...LOBBY_EXIT_BUTTON_SX[MOBILE_NAV_MQ],
                            width: "auto",
                            minWidth: "10.75rem",
                            height: "2.35rem",
                            fontSize: "0.66rem",
                            letterSpacing: "0.1em",
                            px: "1.15rem",
                            mt: "2.15rem",
                        },
                    }}
                >
                    Cancel Search
                </Button>
            ) : (
                <Box
                    role="status"
                    aria-live="polite"
                    sx={{
                        mt: { xs: "1.35rem", xl: "1.5rem" },
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.7rem",
                        px: { xs: "0.85rem", xl: "0.95rem" },
                        py: { xs: "0.55rem", xl: "0.62rem" },
                        maxWidth: "100%",
                        borderRadius: "10px",
                        border: `1px solid ${LOBBY_OURO_BORDER}`,
                        background: LOBBY_OURO_SURFACE,
                        boxShadow: `
                            inset 0 1px 0 rgba(255,255,255,0.045),
                            inset 0 -18px 28px rgba(246, 185, 76, 0.028)
                        `,
                        [MOBILE_NAV_MQ]: {
                            mt: "1.15rem",
                            gap: "0.58rem",
                            px: "0.72rem",
                            py: "0.48rem",
                        },
                    }}
                >
                    <Box
                        aria-hidden
                        sx={{
                            width: { xs: "1.55rem", xl: "1.65rem" },
                            height: { xs: "1.55rem", xl: "1.65rem" },
                            flexShrink: 0,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: "7px",
                            border: `1px solid ${alpha(colors.primary.main, 0.2)}`,
                            background: `
                                linear-gradient(
                                    180deg,
                                    ${alpha(colors.primary.main, 0.14)},
                                    rgba(255,255,255,0.025)
                                )
                            `,
                            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.05)`,
                            [MOBILE_NAV_MQ]: {
                                width: "1.4rem",
                                height: "1.4rem",
                            },
                        }}
                    >
                        <Star
                            sx={{
                                fontSize: { xs: "0.82rem", xl: "0.88rem" },
                                color: colors.primary.main,
                            }}
                        />
                    </Box>
                    <Box sx={{ minWidth: 0, textAlign: "left" }}>
                        <Typography
                            component="p"
                            sx={{
                                m: 0,
                                fontSize: { xs: "0.62rem", xl: "0.66rem" },
                                fontWeight: 700,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                                color: alpha(colors.primary.main, 0.92),
                                lineHeight: 1.2,
                                [MOBILE_NAV_MQ]: {
                                    fontSize: "0.58rem",
                                    letterSpacing: "0.12em",
                                },
                            }}
                        >
                            Waiting on leader
                        </Typography>
                        <Typography
                            component="p"
                            sx={{
                                m: 0,
                                mt: "0.18rem",
                                fontSize: { xs: "0.66rem", xl: "0.7rem" },
                                fontWeight: 500,
                                lineHeight: 1.3,
                                color: alpha(colors.text.secondary, 0.68),
                                [MOBILE_NAV_MQ]: {
                                    fontSize: "0.6rem",
                                },
                            }}
                        >
                            Only the leader can cancel
                        </Typography>
                    </Box>
                </Box>
            )}
            </Box>
            </Box>

            <SmallerMatchOfferDialog
                open={
                    showSmallerMatchOffer &&
                    smallerMatchStatus === "offered" &&
                    canActOnSmallerMatch
                }
                matchLabel={smallerMatchLabel}
                onAccept={onSmallerMatchOptIn}
                onContinue5v5={onSmallerMatchDecline}
            />

            <Box
                sx={{
                    flex: "0 0 auto",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0.35rem",
                    mt: "auto",
                    pt: { xs: "2rem", xl: "2.4rem" },
                    [MOBILE_NAV_MQ]: {
                        pt: "1.35rem",
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        pt: "1rem",
                    },
                }}
            >
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <LobbySearchingInfoBar
                    isParty={isParty}
                    members={members}
                    maxPlayers={maxPlayers}
                    regionsLabel={regionsLabel}
                    averageElo={averageElo}
                    playersSearching={playersSearching}
                    serversOnline={serversOnline}
                    compatibleCount={compatibleCount}
                    mapsLabel={mapsLabel}
                />
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.55rem",
                    minHeight: "2.4rem",
                    maxWidth: "28rem",
                    px: "0.5rem",
                    opacity: tipOpaque ? 1 : 0,
                    transition: `opacity ${TIP_FADE_MS}ms ease`,
                    "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                    },
                }}
            >
                <LobbySectionDiamond size="0.35rem" />
                <Typography
                    component="p"
                    sx={{
                        m: 0,
                        fontSize: { xs: "0.72rem", xl: "0.78rem" },
                        fontWeight: 500,
                        lineHeight: 1.45,
                        color: alpha(colors.text.secondary, 0.72),
                        textAlign: "left",
                        [MOBILE_NAV_MQ]: {
                            fontSize: "0.66rem",
                        },
                    }}
                >
                    {SEARCH_TIPS[tipIndex]}
                </Typography>
            </Box>
            </Box>
        </MotionBox>
    );
}

LobbySearchingStatus.propTypes = {
    timestamp: PropTypes.number,
    onCancel: PropTypes.func,
    canCancel: PropTypes.bool,
    isParty: PropTypes.bool,
    members: PropTypes.arrayOf(PropTypes.object),
    maxPlayers: PropTypes.number,
    regionsLabel: PropTypes.string,
    averageElo: PropTypes.number,
    playersSearching: PropTypes.number,
    serversOnline: PropTypes.number,
    compatibleCount: PropTypes.number,
    mapsLabel: PropTypes.string,
    smallerMatchOffer: PropTypes.shape({
        teamSize: PropTypes.number,
        matchSize: PropTypes.number,
        optedInCount: PropTypes.number,
        eligibleAfterSeconds: PropTypes.number,
        status: PropTypes.oneOf(["offered", "opted_in", "declined"]),
    }),
    canActOnSmallerMatch: PropTypes.bool,
    onSmallerMatchOptIn: PropTypes.func,
    onSmallerMatchDecline: PropTypes.func,
};
